import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { SegmentForm } from '@/components/audit/SegmentForm';
import { BacklightForm } from '@/components/audit/BacklightForm';
import { AuditSummary } from '@/components/audit/AuditSummary';
import { api } from '@/lib/api-client';
import { classNames } from '@/lib/utils';
import type { Store, Segment, BacklightStatus } from '@/types';

interface MeasurementData {
    segmentId: string;
    segmentName: string;
    targetShare: number;
    measuredShare: number;
    linearCm?: number;
    imageBlob?: Blob;
    imageUrl?: string;
}

interface BacklightData {
    status: BacklightStatus;
    notes?: string;
    imageBlob: Blob;
    imageUrl: string;
}

type AuditStep = 'segments' | 'backlight' | 'summary' | 'success';

export function Audit() {
    const { storeId } = useParams<{ storeId: string }>();
    const navigate = useNavigate();

    // Estado da loja e segmentos
    const [store, setStore] = useState<Store | null>(null);
    const [segments, setSegments] = useState<Segment[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Estado do wizard
    const [currentStep, setCurrentStep] = useState<AuditStep>('segments');
    const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
    const [measurements, setMeasurements] = useState<Map<string, MeasurementData>>(new Map());
    const [backlight, setBacklight] = useState<BacklightData | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Carregar dados
    useEffect(() => {
        async function loadData() {
            setIsLoading(true);
            setError(null);

            try {
                // Carregar loja
                const storeResponse = await api.get<Store>(`/stores/${storeId}`);
                if (!storeResponse.success || !storeResponse.data) {
                    setError('Loja não encontrada');
                    return;
                }
                setStore(storeResponse.data);

                // Carregar segmentos
                const segmentsResponse = await api.get<Segment[]>('/segments');
                if (segmentsResponse.success && segmentsResponse.data) {
                    setSegments(segmentsResponse.data);
                }
            } catch (err) {
                setError('Erro ao carregar dados');
            } finally {
                setIsLoading(false);
            }
        }

        if (storeId) {
            loadData();
        }
    }, [storeId]);

    // Segmento atual
    const currentSegment = segments[currentSegmentIndex];

    // Total de passos
    const totalSteps = segments.length + (store?.hasBacklight ? 1 : 0) + 1; // +1 para summary
    const currentStepNumber = currentStep === 'segments'
        ? currentSegmentIndex + 1
        : currentStep === 'backlight'
            ? segments.length + 1
            : totalSteps;

    // Progresso
    const progress = (currentStepNumber / totalSteps) * 100;

    // Handler para salvar medição
    const handleSegmentSubmit = (data: { measuredShare: number; linearCm?: number; imageBlob?: Blob; imageUrl?: string }) => {
        if (!currentSegment) return;

        const measurementData: MeasurementData = {
            segmentId: currentSegment.id,
            segmentName: currentSegment.name,
            targetShare: currentSegment.targetShare,
            ...data,
        };

        setMeasurements(prev => new Map(prev).set(currentSegment.id, measurementData));

        // Próximo segmento ou próxima etapa
        if (currentSegmentIndex < segments.length - 1) {
            setCurrentSegmentIndex(prev => prev + 1);
        } else if (store?.hasBacklight) {
            setCurrentStep('backlight');
        } else {
            setCurrentStep('summary');
        }
    };

    // Handler para voltar no segmento
    const handleSegmentBack = () => {
        if (currentSegmentIndex > 0) {
            setCurrentSegmentIndex(prev => prev - 1);
        } else {
            navigate('/');
        }
    };

    // Handler para salvar backlight
    const handleBacklightSubmit = (data: BacklightData) => {
        setBacklight(data);
        setCurrentStep('summary');
    };

    // Handler para voltar do backlight
    const handleBacklightBack = () => {
        setCurrentSegmentIndex(segments.length - 1);
        setCurrentStep('segments');
    };

    // Handler para voltar do summary
    const handleSummaryBack = () => {
        if (store?.hasBacklight) {
            setCurrentStep('backlight');
        } else {
            setCurrentSegmentIndex(segments.length - 1);
            setCurrentStep('segments');
        }
    };

    // Handler para confirmar e enviar
    const handleConfirm = async () => {
        setIsSubmitting(true);

        try {
            // 1. Criar auditoria
            const auditResponse = await api.post<{ id: string }>('/audits', {
                storeId,
            });

            if (!auditResponse.success || !auditResponse.data) {
                throw new Error(auditResponse.error || 'Erro ao criar auditoria');
            }

            const auditId = auditResponse.data.id;
            const uploadPromises: Promise<void>[] = [];

            // 2. Processar segmentos medidos
            const measurementsToSave = Array.from(measurements.values());

            // Função auxiliar para processar medição
            const processMeasurement = async (m: MeasurementData) => {
                let imageUrl = m.imageUrl;

                // Se tiver blob, fazer upload
                if (m.imageBlob) {
                    try {
                        // Importar compressão dinamicamente para não pesar bundle inicial
                        const { compressImage } = await import('@/lib/compress');
                        const compressedBlob = await compressImage(m.imageBlob);

                        const uploadResponse = await api.upload<{ url: string; path: string }>(
                            '/upload',
                            compressedBlob,
                            `segment-${m.segmentId}.jpg`,
                            { auditId, type: 'shelf' }
                        );

                        if (uploadResponse.success && uploadResponse.data) {
                            // Adicionar auditId e type ao corpo do upload (api-client precisa suportar campos extras ou passamos via query/header?
                            // O endpoint espera body fields. O api-client.upload usa FormData.
                            // Vamos precisar ajustar o api-client para aceitar campos extras no FormData
                            // Por enquanto, assumindo que api-client.upload é genérico o suficiente ou vamos ajustar
                            imageUrl = uploadResponse.data.url;
                        }
                    } catch (err) {
                        console.error('Erro upload imagem segmento:', err);
                        // Continua sem imagem ou usa a URL anterior se houver
                    }
                }

                // Salvar medição
                await api.post('/measurements', {
                    auditId,
                    segmentId: m.segmentId,
                    measuredShare: m.measuredShare,
                    linearCm: m.linearCm,
                    imageUrl,
                });
            };

            // Adicionar promessas de medições
            measurementsToSave.forEach(m => uploadPromises.push(processMeasurement(m)));

            // 3. Processar backlight se houver
            if (store?.hasBacklight && backlight) {
                const processBacklight = async () => {
                    let imageUrl = backlight.imageUrl;

                    if (backlight.imageBlob) {
                        try {
                            const { compressImage } = await import('@/lib/compress');
                            const compressedBlob = await compressImage(backlight.imageBlob);

                            // Ajustar api-client para enviar campos extras (auditId)
                            // Como o api-client atual não suporta dados extras no upload, 
                            // vamos precisar fazer uma chamada customizada ou alterar o api-client.
                            // VOU ALTERAR O API CLIENT DEPOIS. Por enquanto uso o endpoint upload

                            // Hack: O endpoint upload do backend espera 'auditId' no body.
                            // O api client só envia 'file'.
                            // Preciso atualizar o api-client.ts primeiro.
                            const uploadResponse = await api.upload<{ url: string }>(
                                '/upload',
                                compressedBlob,
                                'backlight.jpg',
                                { auditId, type: 'backlight' }
                            );

                            if (uploadResponse.success && uploadResponse.data) {
                                imageUrl = uploadResponse.data.url;
                            }
                        } catch (err) {
                            console.error('Erro upload backlight:', err);
                        }
                    }

                    await api.post('/backlight', {
                        auditId,
                        status: backlight.status,
                        notes: backlight.notes,
                        imageUrl,
                    });
                };

                uploadPromises.push(processBacklight());
            }

            // Aguardar todos uploads e salvamentos (pode ser otimizado para não bloquear tudo se um falhar)
            // Mas para garantir integridade, vamos esperar.
            await Promise.all(uploadPromises);

            // Atualizar status da auditoria para completed (se necessário, ou o backend já faz)
            // await api.patch(`/audits/${auditId}`, { status: 'completed' });

            setCurrentStep('success');
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Erro ao enviar auditoria');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Medições como array para o summary
    const measurementsArray = useMemo(() =>
        Array.from(measurements.values()),
        [measurements]
    );

    // Loading
    if (isLoading) {
        return (
            <div className="min-h-screen bg-page-bg flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-content-muted">Carregando...</p>
                </div>
            </div>
        );
    }

    // Erro
    if (error && currentStep !== 'success') {
        return (
            <div className="min-h-screen bg-page-bg flex items-center justify-center p-4">
                <Card className="max-w-sm w-full">
                    <CardContent className="text-center">
                        <svg className="w-16 h-16 text-status-danger mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <p className="text-content-body mb-4">{error}</p>
                        <Button onClick={() => navigate('/')} fullWidth>
                            Voltar
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Sucesso
    if (currentStep === 'success') {
        return (
            <div className="min-h-screen bg-page-bg flex items-center justify-center p-4">
                <Card className="max-w-sm w-full">
                    <CardContent className="text-center py-8">
                        <div className="w-20 h-20 bg-status-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-10 h-10 text-status-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-content-title mb-2">
                            Auditoria Enviada!
                        </h2>
                        <p className="text-content-muted mb-6">
                            A auditoria de {store?.name} foi registrada com sucesso.
                        </p>
                        <Button onClick={() => navigate('/')} fullWidth size="lg">
                            Voltar ao Início
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-page-bg">
            {/* Header */}
            <header className="bg-brand-header text-content-inverse sticky top-0 z-10">
                <div className="max-w-lg mx-auto px-4 py-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => currentStep === 'segments' && currentSegmentIndex === 0 ? navigate('/') : handleSegmentBack()}
                            className="p-1 hover:bg-white/10 rounded"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <div className="flex-1">
                            <h1 className="font-semibold">{store?.name}</h1>
                            <p className="text-sm opacity-80">
                                Passo {currentStepNumber} de {totalSteps}
                            </p>
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-3 w-full bg-white/20 rounded-full h-1.5">
                        <div
                            className="bg-white h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="max-w-lg mx-auto px-4 py-6 pb-24">
                {currentStep === 'segments' && currentSegment && (
                    <SegmentForm
                        segment={currentSegment}
                        initialValue={measurements.get(currentSegment.id)}
                        onSubmit={handleSegmentSubmit}
                        onBack={currentSegmentIndex > 0 ? handleSegmentBack : undefined}
                    />
                )}

                {currentStep === 'backlight' && (
                    <BacklightForm
                        onSubmit={handleBacklightSubmit}
                        onBack={handleBacklightBack}
                    />
                )}

                {currentStep === 'summary' && store && (
                    <AuditSummary
                        storeName={store.name}
                        measurements={measurementsArray}
                        backlight={backlight || undefined}
                        onConfirm={handleConfirm}
                        onBack={handleSummaryBack}
                        isSubmitting={isSubmitting}
                    />
                )}
            </main>
        </div>
    );
}

export default Audit;
